import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { 
  FileSpreadsheet, Users, CheckCircle, Phone, ChevronRight, 
  AlertTriangle, Eye, Loader2, Smartphone, MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactFileSelector({ 
  open, 
  onOpenChange, 
  onSelectFile,
  templateVariables = [] // Variables from the selected template
}) {
  const { token } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileDetails, setFileDetails] = useState(null);
  const [step, setStep] = useState('select'); // 'select' | 'preview' | 'mapping'
  const [variableMapping, setVariableMapping] = useState({});

  // Fetch contact files
  const fetchFiles = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/contacts/files?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFiles(data.files);
      }
    } catch (err) {
      console.error('Error fetching files:', err);
    } finally {
      setLoading(false);
    }
  }, [token, open]);

  useEffect(() => {
    if (open) {
      fetchFiles();
      setStep('select');
      setSelectedFile(null);
      setFileDetails(null);
      setVariableMapping({});
    }
  }, [open, fetchFiles]);

  // Fetch file details for campaign (with RCS/SMS split)
  const fetchFileDetails = async (fileId) => {
    try {
      // Use the split endpoint to get RCS/SMS breakdown
      const response = await fetch(`${API_URL}/api/contacts/files/${fileId}/for-campaign-split`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFileDetails(data);
        
        // Auto-map variables if names match
        const autoMapping = {};
        templateVariables.forEach(templateVar => {
          const matchingCol = data.variable_columns.find(
            col => col.toLowerCase() === templateVar.toLowerCase()
          );
          if (matchingCol) {
            autoMapping[templateVar] = matchingCol;
          }
        });
        setVariableMapping(autoMapping);
        
        setStep('preview');
      }
    } catch (err) {
      console.error('Error fetching file details:', err);
    }
  };

  const handleSelectFile = (file) => {
    setSelectedFile(file);
    fetchFileDetails(file.id);
  };

  const handleConfirm = () => {
    if (!fileDetails) return;
    
    // Combine RCS and SMS contacts
    const allContacts = [
      ...fileDetails.rcs_contacts.contacts,
      ...fileDetails.sms_contacts.contacts
    ];
    
    onSelectFile({
      file_id: fileDetails.file_id,
      file_name: fileDetails.name,
      contacts: allContacts,
      total_valid: fileDetails.total_valid,
      rcs_count: fileDetails.rcs_contacts.count,
      sms_count: fileDetails.sms_contacts.count,
      rcs_check_done: fileDetails.rcs_check_done,
      variable_columns: fileDetails.variable_columns,
      variable_mapping: variableMapping
    });
    
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="contact-file-selector">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
            {step === 'select' && 'Sélectionner un fichier de contacts'}
            {step === 'preview' && (
              <>
                <button 
                  onClick={() => setStep('select')} 
                  className="text-slate-400 hover:text-slate-600"
                >
                  Fichiers
                </button>
                <ChevronRight className="w-4 h-4 text-slate-400" />
                {selectedFile?.name}
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {step === 'select' && 'Choisissez un fichier importé pour votre campagne'}
            {step === 'preview' && fileDetails?.rcs_check_done 
              ? `${fileDetails.rcs_contacts?.count || 0} RCS + ${fileDetails.sms_contacts?.count || 0} SMS`
              : `${fileDetails?.total_valid || 0} contacts valides seront utilisés`
            }
          </DialogDescription>
        </DialogHeader>

        {/* Select Step */}
        {step === 'select' && (
          <ScrollArea className="h-[400px]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : files.length === 0 ? (
              <div className="text-center py-12">
                <FileSpreadsheet className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <p className="text-slate-500">Aucun fichier importé</p>
                <p className="text-sm text-slate-400 mt-1">
                  Importez d'abord des contacts depuis la page Contacts
                </p>
              </div>
            ) : (
              <div className="space-y-2 pr-4">
                {files.map(file => (
                  <div
                    key={file.id}
                    onClick={() => handleSelectFile(file)}
                    className="p-4 rounded-xl border-2 cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/50 transition-all group"
                    data-testid={`contact-file-${file.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium group-hover:text-indigo-700">
                          {file.name}
                          {file.rcs_check_done && (
                            <Badge className="ml-2 bg-indigo-100 text-indigo-700 text-[10px]">
                              RCS vérifié
                            </Badge>
                          )}
                        </p>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <Badge variant="secondary" className="gap-1">
                            <Users className="w-3 h-3" />
                            {file.total_contacts} contacts
                          </Badge>
                          <Badge 
                            variant="outline" 
                            className={file.valid_contacts === file.total_contacts ? 'text-green-600 border-green-200' : 'text-amber-600 border-amber-200'}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {file.valid_contacts} valides
                          </Badge>
                          {file.rcs_check_done && (
                            <>
                              <Badge className="bg-green-100 text-green-700 gap-1">
                                <Smartphone className="w-3 h-3" />
                                {file.rcs_capable_count} RCS
                              </Badge>
                              <Badge className="bg-orange-100 text-orange-700 gap-1">
                                <MessageSquare className="w-3 h-3" />
                                {file.sms_fallback_count} SMS
                              </Badge>
                            </>
                          )}
                          {file.invalid_contacts > 0 && (
                            <Badge variant="outline" className="text-red-600 border-red-200">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              {file.invalid_contacts} invalides
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-slate-400">Colonnes:</span>
                          {file.columns?.slice(0, 4).map(col => (
                            <Badge key={col} variant="secondary" className="text-[10px] py-0">
                              {col}
                            </Badge>
                          ))}
                          {file.columns?.length > 4 && (
                            <Badge variant="secondary" className="text-[10px] py-0">
                              +{file.columns.length - 4}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        )}

        {/* Preview Step */}
        {step === 'preview' && fileDetails && (
          <div className="space-y-4">
            {/* Stats with RCS/SMS split */}
            <div className={`grid gap-3 ${fileDetails.rcs_check_done ? 'grid-cols-4' : 'grid-cols-3'}`}>
              <div className="bg-indigo-50 rounded-xl p-4 text-center">
                <Users className="w-6 h-6 text-indigo-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-indigo-700">{fileDetails.total_valid}</p>
                <p className="text-xs text-indigo-600">Total valides</p>
              </div>
              {fileDetails.rcs_check_done ? (
                <>
                  <div className="bg-green-50 rounded-xl p-4 text-center border border-green-200">
                    <Smartphone className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-green-700">{fileDetails.rcs_contacts?.count || 0}</p>
                    <p className="text-xs text-green-600">RCS</p>
                  </div>
                  <div className="bg-orange-50 rounded-xl p-4 text-center border border-orange-200">
                    <MessageSquare className="w-6 h-6 text-orange-600 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-orange-700">{fileDetails.sms_contacts?.count || 0}</p>
                    <p className="text-xs text-orange-600">SMS Fallback</p>
                  </div>
                </>
              ) : (
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <Phone className="w-6 h-6 text-slate-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-slate-700">{fileDetails.mobile_column}</p>
                  <p className="text-xs text-slate-600">Colonne mobile</p>
                </div>
              )}
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <FileSpreadsheet className="w-6 h-6 text-purple-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-purple-700">{fileDetails.variable_columns?.length || 0}</p>
                <p className="text-xs text-purple-600">Variables</p>
              </div>
            </div>

            {/* RCS check not done warning */}
            {!fileDetails.rcs_check_done && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-slate-500" />
                <div>
                  <p className="text-sm text-slate-700">Compatibilité RCS non vérifiée</p>
                  <p className="text-xs text-slate-500">Vérifiez la compatibilité depuis la page Contacts pour voir le split RCS/SMS</p>
                </div>
              </div>
            )}

            {/* Variable Mapping (if template has variables) */}
            {templateVariables.length > 0 && fileDetails.variable_columns?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="font-medium text-amber-800 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Mapping des variables
                </p>
                <p className="text-sm text-amber-700 mb-3">
                  Associez les colonnes du fichier aux variables du template
                </p>
                <div className="space-y-2">
                  {templateVariables.map(templateVar => (
                    <div key={templateVar} className="flex items-center gap-3">
                      <Badge className="bg-indigo-600 min-w-[100px] justify-center">
                        {`{${templateVar}}`}
                      </Badge>
                      <span className="text-slate-400">→</span>
                      <select
                        value={variableMapping[templateVar] || ''}
                        onChange={(e) => setVariableMapping(prev => ({
                          ...prev,
                          [templateVar]: e.target.value
                        }))}
                        className="flex-1 border rounded-lg px-3 py-1.5 text-sm"
                      >
                        <option value="">-- Non mappé --</option>
                        {fileDetails.variable_columns.map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Preview contacts */}
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">
                Aperçu des 5 premiers contacts
              </p>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Mobile</th>
                      {fileDetails.variable_columns?.slice(0, 3).map(col => (
                        <th key={col} className="px-3 py-2 text-left font-medium text-slate-600">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fileDetails.contacts?.slice(0, 5).map((contact, idx) => (
                      <tr key={idx} className="border-b last:border-0">
                        <td className="px-3 py-2 font-mono text-xs">{contact.mobile}</td>
                        {fileDetails.variable_columns?.slice(0, 3).map(col => (
                          <td key={col} className="px-3 py-2 text-xs">{contact.variables?.[col] || '—'}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 'select' && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setStep('select')}>
                Retour
              </Button>
              <Button 
                onClick={handleConfirm}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="confirm-file-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Utiliser ce fichier ({fileDetails?.total_valid} contacts)
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
